import { serverUrl } from '../config';

/**
 * Onboarding Settings Service
 *
 * Generic "discover / confirm" pattern used by ad platforms (Meta, and future
 * platforms like Google, TikTok, etc.) to let users self-service select the
 * platform entities (ad account, page, pixel, etc.) that should be linked to
 * their Limitless account after OAuth succeeds.
 *
 * Each platform exposes two endpoints following the same contract:
 *   POST /{platform}/onboarding/discover  -> { status, discovered, options, warnings, onboardingToken? }
 *   POST /{platform}/onboarding/confirm   -> { status, savedKeys, missingKeys, ...adApiStatus }
 *
 * Both endpoints accept either:
 *   - Authorization: JWT <token>            (logged-in user), or
 *   - Authorization: JWT <onboardingToken>   (short-lived token issued for
 *     users completing onboarding without being logged in)
 */

const buildAuthHeader = (token) => (token ? { authorization: `JWT ${token}` } : {});

/**
 * Discover available platform settings/options for an account.
 * @param {Object} params
 * @param {string} params.platform - Platform identifier (e.g., 'meta')
 * @param {number|string} params.accountId - Limitless account ID
 * @param {string} [params.token] - JWT (logged-in user) or onboardingToken
 */
const discoverSettings = async ({ platform, accountId, token }) => {
  const url = `${serverUrl}/${platform}/onboarding/discover`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...buildAuthHeader(token),
    },
    body: JSON.stringify({ accountId }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to discover onboarding settings');
  }

  return response.json();
};

/**
 * Confirm/save the user-selected platform settings for an account.
 * @param {Object} params
 * @param {string} params.platform - Platform identifier (e.g., 'meta')
 * @param {number|string} params.accountId - Limitless account ID
 * @param {Object} params.selections - Key/value map of selected settings (e.g. { adAccountId, pageId, ... })
 * @param {string} [params.token] - JWT (logged-in user) or onboardingToken
 */
const confirmSettings = async ({
  platform, accountId, selections, token,
}) => {
  const url = `${serverUrl}/${platform}/onboarding/confirm`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...buildAuthHeader(token),
    },
    body: JSON.stringify({ accountId, ...selections }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to confirm onboarding settings');
  }

  return response.json();
};

const onboardingSettingsService = {
  discoverSettings,
  confirmSettings,
};

export default onboardingSettingsService;
